import { ReactNode, useState } from 'react'
import { Text, UnstyledButton, Group, Box, Collapse, ScrollArea, Divider, Badge, Flex, Container, Tooltip } from '@mantine/core'
import { useLocation, Link } from 'react-router-dom'
import {
    IconDeviceGamepad2,
    IconDevices,
    IconShoppingCart,
    IconCurrency,
    IconTrendingUp,
    IconMail,
    IconUsers,
    IconMessageCircle,
    IconBan,
    IconSettings,
    IconHelpCircle,
    IconPhoto, IconChevronRight,
    IconChevronDown,
    IconShield,
    IconHome,
    IconCalendar, IconEye, IconMenu2
} from '@tabler/icons-react'
import { useI18n } from '@/hooks/useI18n'
import { useDarkMode } from '@/hooks/useDarkMode'

interface AdminLayoutProps {
    children: ReactNode
}

interface NavItem {
    label: string
    icon: any
    color: string
    links?: Array<{
        label: string
        href: string
        icon?: any
        badge?: string
    }>
    href?: string
    badge?: string
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    const { t } = useI18n()
    const { colorScheme } = useDarkMode()
    const location = useLocation()
    const [openedSections, setOpenedSections] = useState<string[]>([])
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

    const navItems: NavItem[] = [
        {
            label: 'Dashboard',
            icon: IconHome,
            color: 'blue',
            href: '/admin',
        },
        {
            label: t('admin.games.title'),
            icon: IconDeviceGamepad2,
            color: 'blue',
            href: '/admin/games',
        },
        {
            label: 'Game Platforms',
            icon: IconDevices,
            color: 'cyan',
            links: [
                { label: 'View All', href: '/admin/game-platforms', icon: IconEye },
                { label: 'By Release Date', href: '/admin/game-platforms?sortBy=releaseDate&sortOrder=desc', icon: IconCalendar },
                { label: 'PS5 only', href: '/admin/game-platforms?platform=ps5', icon: IconDeviceGamepad2 },
            ]
        },
        {
            label: 'Game Platform Images',
            icon: IconPhoto,
            color: 'violet',
            links: [
                { label: 'View All', href: '/admin/game-platform-images', icon: IconEye },
                { label: 'Landscape Images', href: '/admin/game-platform-images/landscape', icon: IconPhoto },
            ]
        },
        {
            label: t('admin.platforms.title'),
            icon: IconDevices,
            color: 'green',
            href: '/admin/platforms',
        },
        {
            label: t('admin.stores.title'),
            icon: IconShoppingCart,
            color: 'orange',
            href: '/admin/stores',
        },
        {
            label: t('admin.prices.title'),
            icon: IconCurrency,
            color: 'red',
            href: '/admin/prices'
        },
        {
            label: 'Sales',
            icon: IconCurrency,
            color: 'teal',
            href: '/admin/sales'
        },
        {
            label: 'Analytics',
            icon: IconTrendingUp,
            color: 'purple',
            links: [
                { label: 'Overview', href: '/admin/analytics', icon: IconEye },
                { label: 'Popular Games', href: '/admin/analytics?view=games', icon: IconTrendingUp },
                { label: 'Platform Stats', href: '/admin/analytics?view=platforms', icon: IconDevices },
                { label: 'Store Clicks', href: '/admin/analytics?view=stores', icon: IconShoppingCart },
            ]
        },
        {
            label: 'Email Templates',
            icon: IconMail,
            color: 'teal',
            href: '/admin/email-templates',
        },
        {
            label: 'User Management',
            icon: IconUsers,
            color: 'indigo',
            href: '/admin/users'
        },
        {
            label: 'Comment Management',
            icon: IconMessageCircle,
            color: 'teal',
            links: [
                { label: 'View All Comments', href: '/admin/comments', icon: IconEye },
                { label: 'Comment Settings', href: '/admin/comment-settings', icon: IconSettings },
                { label: 'Censored Words', href: '/admin/censored-words', icon: IconBan },
            ]
        },
        {
            label: t('faq.title'),
            icon: IconHelpCircle,
            color: 'yellow',
            href: '/admin/faqs'
        },
    ]

    const toggleSection = (label: string) => {
        setOpenedSections(prev =>
            prev.includes(label)
                ? prev.filter(item => item !== label)
                : [...prev, label]
        )
    }

    const isActive = (href: string) => {
        if (href === '/admin') {
            return location.pathname === '/admin'
        }
        return location.pathname.startsWith(href.split('?')[0])
    }

    const isLinkActive = (href: string) => {
        const path = href.split('?')[0]
        const search = href.split('?')[1]

        if (search) {
            return location.pathname === path && location.search.includes(search.split('=')[0])
        }
        return location.pathname === path
    }

    const NavItemComponent = ({ item }: { item: NavItem }) => {
        const hasLinks = item.links && item.links.length > 0
        const isOpen = openedSections.includes(item.label)
        const isActiveItem = item.href ? isActive(item.href) : false

        if (hasLinks) {
            if (sidebarCollapsed) {
                return (
                    <Tooltip key={item.label} label={item.label} position="right" withArrow>
                        <UnstyledButton
                            component={Link}
                            to={item.links![0].href}
                            style={(theme) => ({
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
                                borderRadius: theme.radius.sm,
                                color: colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                                backgroundColor: isActiveItem ? theme.colors.blue[0] : 'transparent',
                                textDecoration: 'none',
                                '&:hover': {
                                    backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                                },
                            })}
                        >
                            <item.icon size={32} color={item.color} stroke={0.8} />
                        </UnstyledButton>
                    </Tooltip>
                )
            }

            return (
                <Box key={item.label}>
                    <UnstyledButton
                        onClick={() => toggleSection(item.label)}
                        style={(theme) => ({
                            display: 'block',
                            width: '100%',
                            padding: theme.spacing.xs,
                            borderRadius: theme.radius.sm,
                            color: colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                            backgroundColor: isActiveItem ? theme.colors.blue[0] : 'transparent',
                            '&:hover': {
                                backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                            },
                        })}
                    >
                        <Group justify="space-between">
                            <Group gap="sm">
                                <item.icon size={20} color={item.color} />
                                <Text size="sm" fw={500}>
                                    {item.label}
                                </Text>
                            </Group>
                            {isOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                        </Group>
                    </UnstyledButton>

                    <Collapse in={isOpen}>
                        <Box ml={20} mt={5}>
                            {item.links!.map((link) => (
                                <UnstyledButton
                                    key={link.href}
                                    component={Link}
                                    to={link.href}
                                    style={(theme) => ({
                                        display: 'block',
                                        width: '100%',
                                        padding: theme.spacing.xs,
                                        borderRadius: theme.radius.sm,
                                        color: colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                                        backgroundColor: isLinkActive(link.href) ? theme.colors.blue[0] : 'transparent',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                                        },
                                    })}
                                >
                                    <Group gap="sm">
                                        {link.icon && <link.icon size={16} />}
                                        <Text size="sm">
                                            {link.label}
                                        </Text>
                                        {link.badge && (
                                            <Badge size="xs" variant="light" color="blue">
                                                {link.badge}
                                            </Badge>
                                        )}
                                    </Group>
                                </UnstyledButton>
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )
        }

        if (sidebarCollapsed) {
            return (
                <Tooltip key={item.label} label={item.label} position="right" withArrow>
                    <UnstyledButton
                        component={Link}
                        to={item.href!}
                        style={(theme) => ({
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
                            borderRadius: theme.radius.sm,
                            color: colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                            backgroundColor: isActiveItem ? theme.colors.blue[0] : 'transparent',
                            textDecoration: 'none',
                            '&:hover': {
                                backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                            },
                        })}
                    >
                        <item.icon size={32} color={item.color} stroke={0.8} />
                    </UnstyledButton>
                </Tooltip>
            )
        }

        return (
            <UnstyledButton
                key={item.label}
                component={Link}
                to={item.href!}
                style={(theme) => ({
                    display: 'block',
                    width: '100%',
                    padding: theme.spacing.xs,
                    borderRadius: theme.radius.sm,
                    color: colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                    backgroundColor: isActiveItem ? theme.colors.blue[0] : 'transparent',
                    textDecoration: 'none',
                    '&:hover': {
                        backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                    },
                })}
            >
                <Group gap="sm">
                    <item.icon size={20} color={item.color} />
                    <Text size="sm" fw={500}>
                        {item.label}
                    </Text>
                    {item.badge && (
                        <Badge size="xs" variant="light" color="blue">
                            {item.badge}
                        </Badge>
                    )}
                </Group>
            </UnstyledButton>
        )
    }

    return (
        <Container size="xl" py="xl">
            <Flex style={{ minHeight: 'calc(100vh - 200px)' }}>
                {/* Sidebar */}
                <Box
                    w={sidebarCollapsed ? 80 : 300}
                    style={(theme) => ({
                        borderRight: `1px solid ${colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                        backgroundColor: colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
                        minHeight: 'calc(100vh - 200px)',
                        position: 'sticky',
                        top: 0,
                        overflowY: 'auto',
                        transition: 'width 0.2s ease'
                    })}
                >
                    <Box p="md">
                        <Group mb="md" justify={sidebarCollapsed ? "center" : "space-between"}>
                            {!sidebarCollapsed && (
                                <>
                                    <IconShield size={24} color="blue" />
                                    <Text fw={700} size="lg">
                                        Admin Panel
                                    </Text>
                                </>
                            )}
                            {sidebarCollapsed && <IconShield size={24} color="blue" />}
                            <Tooltip label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"} position="right" withArrow>
                                <UnstyledButton
                                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                    style={(theme) => ({
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: theme.spacing.xs,
                                        borderRadius: theme.radius.sm,
                                        color: colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                                        '&:hover': {
                                            backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                                        },
                                    })}
                                >
                                    <IconMenu2 size={16} />
                                </UnstyledButton>
                            </Tooltip>
                        </Group>

                        <ScrollArea h="calc(100vh - 280px)">
                            <Box>
                                {navItems.map((item, index) => (
                                    <Box key={item.label}>
                                        <NavItemComponent item={item} />
                                        {index < navItems.length - 1 && <Divider my="xs" />}
                                    </Box>
                                ))}
                            </Box>
                        </ScrollArea>
                    </Box>
                </Box>

                {/* Main Content */}
                <Box style={{ flex: 1, overflowY: 'auto' }} m={20}>
                    {children}
                </Box>
            </Flex>
        </Container>
    )
} 